import React, { useState } from "react";
import styles from "./styles.module.scss";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import { SubscriptionModel } from "app/models";
import ButtonVariant from "app/components/Button/ButtonVariant";
import { useTranslation } from "react-i18next";
import { useActions } from "app/utils/hooks";
import { cancelProTrialSubscription } from "app/actions/subscription";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: SubscriptionModel;
  onCancelSuccess?: () => void;
  // Set when the subscription is an organization's pool rather than the
  // coach's own: cancelling it ends every seat the organization pays for
  organizationName?: string;
}

const CancelProTrialSubscriptionModal = ({
  isOpen,
  onClose,
  subscription,
  onCancelSuccess,
  organizationName,
}: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [pending, setPending] = useState(false);

  const subscriptionActions = useActions({
    cancelProTrialSubscription,
  });

  const onSubscriptionCancel = async () => {
    try {
      setPending(true);
      await subscriptionActions.cancelProTrialSubscription();

      if (onCancelSuccess) {
        onCancelSuccess();
      }

      toast.success(t("Subscription cancelled successfully."));
      setPending(false);
      onClose();
      history.push("/");
    } catch (err) {
      toast.error(
        t("Something went wrong. Please try again later or contact support."),
      );
      setPending(false);
    }
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.title}>{t("Cancel Subscription")}</div>
        <div className={styles.info_wrapper}>
          {t(
            "Are you sure you want to cancel {{subscriptionName}} subscription?",
            { subscriptionName: subscription?.planName },
          )}
          {organizationName && (
            <p className={styles.pool_note}>
              {t(
                "This subscription belongs to {{name}} and pays for every seat of its pool. All of them end with it at the end of the current period.",
                { name: organizationName },
              )}
            </p>
          )}
        </div>
        <div className={styles.buttons_container}>
          <ButtonVariant
            title={t("Yes")}
            buttonType="danger"
            disabled={pending}
            onClickFunc={onSubscriptionCancel}
            buttonStyles={styles.confirm_button}
          />
          <ButtonVariant
            title={t("No")}
            disabled={pending}
            buttonType="secondary-outline"
            onClickFunc={onClose}
            buttonStyles={styles.cancel_button}
          />
        </div>
      </div>
    </ModalLayout>
  );
};

export default CancelProTrialSubscriptionModal;
